import {
  ClerkProvider,
  SignIn,
  SignedIn,
  SignedOut,
  useAuth,
} from "@clerk/clerk-react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ActionItemsProvider } from "contexts/ActionItems";
import { AdminUserProvider } from "contexts/Admin";
import { AgentSessionsProvider } from "contexts/AgentSessions";
import { AlertProvider } from "contexts/Alert";
import { AutogeneratedDraftsProvider } from "contexts/AutogenerateDrafts";
import { AutomationsProvider } from "contexts/Automations";
import { BentoCategoriesProvider } from "contexts/BentoCategories";
import { BrandsProvider } from "contexts/Brands";
import { CampaignsProvider } from "contexts/Campaigns";
import { ChatHelperProvider } from "contexts/ChatHelper";
import { CheckoutProvider } from "contexts/Checkout";
import { ContactViewProvider } from "contexts/ContactView";
import { ContactViewSearchProvider } from "contexts/ContactViewSearch";
import { DiscoverViewProvider } from "contexts/DiscoverView";
import { MetricsProvider } from "contexts/Metrics";
import { OnboardingDialogsProvider } from "contexts/OnboardingDialogs";
import { OrganizationUserProvider } from "contexts/Organization";
import { OutreachContactsProvider } from "contexts/OutreachContacts";
import { OutreachTemplatesProvider } from "contexts/OutreachTemplates";
import { QuickSendProvider } from "contexts/QuickSend";
import { QuickSendContactsProvider } from "contexts/QuickSendContacts";
import { QuickSendDrawerProvider } from "contexts/QuickSendDrawer";
import { QuickSendEditorProvider } from "contexts/QuickSendEditor";
import { QuickSendErrorProvider } from "contexts/QuickSendError";
import { QuickSendOutreachTabsProvider } from "contexts/QuickSendOutreachTabs";
import { QuickSendPaginationProvider } from "contexts/QuickSendPagination";
import { SavedBrandProvider } from "contexts/SavedBrand";
import { SavedBrandCollectionsProvider } from "contexts/SavedBrandCollections";
import { SubscriptionProvider } from "contexts/Subscription";
import { TasksProvider } from "contexts/Tasks";
import { TopRecommendationsProvider } from "contexts/TopRecommendations";
import { TrackedCreatorsProvider } from "contexts/TrackedCreators";
import { TrackingColumnsProvider } from "contexts/TrackingColumns";
import { TrackingColumnsFiltersProvider } from "contexts/TrackingColumnsFilters";
import { TrackingColumnsSortProvider } from "contexts/TrackingColumnsSort";
import { UserIntegrationsProvider } from "contexts/UserIntegrations";
import { UserRequestsProvider } from "contexts/UserRequests";
import { VASettingsProvider } from "contexts/VaSettings";
import { ReactNode, useEffect, useState } from "react";
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";

import StripeCheckout from "components/StripeCheckout";
import DeprecatedPage from "components/Styled/DeprecatedPage";
import { AdminHome } from "features/Admin";
import AdminBrands from "features/Admin/Brand";
import AdminBrandVerifications from "features/Admin/BrandVerifications";
import AdminClay from "features/Admin/Clay";
import AdminContacts from "features/Admin/Contact";
import AdminCsvUpload from "features/Admin/CsvUpload";
import AdminElasticsearch from "features/Admin/Elasticsearch";
import InstagramPage from "features/Admin/Instagram";
import MergeBrands from "features/Admin/MergeBrands";
import AdminMigrations from "features/Admin/Migrations";
import AdminOrganization from "features/Admin/Organization";
import AdminReadEmail from "features/Admin/ReadEmail";
import AdminScraping from "features/Admin/Scraping";
import ScrapingPreview from "features/Admin/ScrapingPreview";
import SearchBrands from "features/Admin/SearchBrands";
import UploadBrands from "features/Admin/UploadBrands";
import UploadLocal from "features/Admin/UploadLocal";
import UserRequests from "features/Admin/UserRequests";
import VAManagement from "features/Admin/VAManagement";
import AuthPage from "features/Auth";
import AcceptToken from "features/Auth/AcceptToken";
import BrandOnboarding from "features/Auth/BrandOnboarding";
import InstagramOnboarding from "features/Auth/InstagramOnboarding";
import Layout from "features/Auth/Layout";
import OnboardingInfluencer from "features/Auth/Onboarding";
import Onboarding from "features/Auth/OnboardingDemo";
import SelfServeVaOnboarding from "features/Auth/SelfServeVaOnboarding";
import { AuthAction } from "features/Auth/schema";
import FairPaySurvey from "features/FairPaySurvey";
import Home from "features/Home";
import SubscribePage from "features/Home/UpgradeDialog/SubscribePage";
import SuccessPage from "features/Home/UpgradeDialog/SuccessPage";
import CampaignsPage from "features/Influencer/Campaigns";
import InfluencerContacts from "features/Influencer/ContactList";
import Discover from "features/Influencer/Discover";
import DiscoverResults from "features/Influencer/Discover/Results";
import HomePage from "features/Influencer/HomePage";
import InstagramDashboardPage from "features/Influencer/Instagram";
import LocalPitching from "features/Influencer/Local";
import PortfolioSitesPage from "features/Influencer/PortfolioSites";
import Profile from "features/Influencer/Profile";
import RecommendationsPage from "features/Influencer/Recommendations";
import AnnualSplashPage from "features/Influencer/SplashPage/Annual";
import AnnualTemplate from "features/Influencer/SplashPage/AnnualTemplate";
import PitchPlease from "features/Influencer/SplashPage/PitchPlease";
import ProPartnershipBundle from "features/Influencer/SplashPage/ProPartnershipBundle";
import ProPlanSplashPage from "features/Influencer/SplashPage/ProPlan";
import Repitching from "features/Influencer/SplashPage/Repitching";
import StrategyPackageSplashPage from "features/Influencer/SplashPage/StrategyPackage";
import VaAnnualSplashPage from "features/Influencer/SplashPage/VaAnnualSplashPage";
import VaPlanSplashPage from "features/Influencer/SplashPage/VaPlan";
import TemplatesPage from "features/Influencer/Templates";
import VA from "features/Influencer/VA";
import LandingPage from "features/Landing";
import PrivacyPolicyPage from "features/PrivacyPolicy";
import Settings from "features/Settings";
import AddOnTemplatesIntakeForm from "features/Settings/AddOns/AddOnsTemplates/IntakeForm";
import PageNotFound from "pages/PageNotFound";
import WebViewEditor from "pages/WebViewEditor";
import { initSessionReplay, trackEvent } from "utils/tracking";

import "./App.css";

if (!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key");
}

if (!process.env.REACT_APP_ADMIN_CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing Admin Publishable Key");
}

if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
  throw new Error("Missing Google Client ID");
}

if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
  throw new Error("Missing Stripe Publishable Key");
}

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
const adminClerkPubKey = process.env.REACT_APP_ADMIN_CLERK_PUBLISHABLE_KEY;
const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function App() {
  const [trackedInitial, setTrackedInitial] = useState(false);
  useEffect(() => {
    const trackVisit = () => {
      if (!trackedInitial) {
        trackEvent("App Visited");
        setTrackedInitial(true);
      }
    };
    trackVisit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedInitial]);

  return <AppRoutes />;
}

interface ClerkProviderWrapperProps {
  children: ReactNode;
  pubKey: string;
}

const SessionReplayInit = () => {
  const { isSignedIn } = useAuth();
  useEffect(() => {
    if (isSignedIn) {
      initSessionReplay();
    }
  }, [isSignedIn]);
  return null;
};

const ClerkProviderWrapper: React.FC<ClerkProviderWrapperProps> = ({
  children,
  pubKey,
}) => {
  return (
    <ClerkProvider
      publishableKey={pubKey}
      appearance={{
        variables: {
          colorPrimary: "#014339",
          colorText: "#151e16",
        },
      }}
    >
      <SessionReplayInit />
      {children}
    </ClerkProvider>
  );
};

const DashboardProviderWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <ClerkProviderWrapper pubKey={clerkPubKey}>
      <OrganizationUserProvider>
        <AlertProvider>
          <GoogleOAuthProvider clientId={googleClientId}>
            <UserIntegrationsProvider>
              <CheckoutProvider>
                <SubscriptionProvider>
                  <DashboardPagesWrapper>
                    <OnboardingDialogsProvider>
                      <ActionItemsProvider>
                        <QuickSendWrapper>
                          <ChatHelperProvider>
                            <TrackingPagesWrapper>
                              <MetricsProvider>
                                <AgentSessionsProvider>
                                  {children}
                                </AgentSessionsProvider>
                              </MetricsProvider>
                            </TrackingPagesWrapper>
                          </ChatHelperProvider>
                        </QuickSendWrapper>
                      </ActionItemsProvider>
                    </OnboardingDialogsProvider>
                  </DashboardPagesWrapper>
                </SubscriptionProvider>
              </CheckoutProvider>
            </UserIntegrationsProvider>
          </GoogleOAuthProvider>
        </AlertProvider>
      </OrganizationUserProvider>
    </ClerkProviderWrapper>
  );
};

const QuickSendWrapper = ({ children }: { children: ReactNode }) => {
  // Contexts needed for QuickSend
  return (
    <QuickSendDrawerProvider>
      <QuickSendPaginationProvider>
        <QuickSendContactsProvider>
          <QuickSendOutreachTabsProvider>
            <QuickSendErrorProvider>
              <QuickSendProvider>
                <QuickSendEditorProvider>{children}</QuickSendEditorProvider>
              </QuickSendProvider>
            </QuickSendErrorProvider>
          </QuickSendOutreachTabsProvider>
        </QuickSendContactsProvider>
      </QuickSendPaginationProvider>
    </QuickSendDrawerProvider>
  );
};

const DashboardPagesWrapper = ({ children }: { children: ReactNode }) => {
  // Contexts needed for pages inside Dashboard that are not dependent on QuickSend
  return (
    <OutreachTemplatesProvider>
      <BentoCategoriesProvider>
        <BrandsProvider>
          <TopRecommendationsProvider>
            <AutogeneratedDraftsProvider>
              <TrackedCreatorsProvider>
                <DiscoverViewProvider>
                  <SavedBrandCollectionsProvider>
                    <SavedBrandProvider>
                      <TasksProvider>
                        <OutreachContactsProvider>
                          <UserRequestsProvider>
                            <AutomationsProvider>
                              <VASettingsProvider>
                                {children}
                              </VASettingsProvider>
                            </AutomationsProvider>
                          </UserRequestsProvider>
                        </OutreachContactsProvider>
                      </TasksProvider>
                    </SavedBrandProvider>
                  </SavedBrandCollectionsProvider>
                </DiscoverViewProvider>
              </TrackedCreatorsProvider>
            </AutogeneratedDraftsProvider>
          </TopRecommendationsProvider>
        </BrandsProvider>
      </BentoCategoriesProvider>
    </OutreachTemplatesProvider>
  );
};

const TrackingPagesWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <ContactViewProvider>
      <ContactViewSearchProvider>
        <TrackingColumnsProvider>
          <TrackingColumnsFiltersProvider>
            <TrackingColumnsSortProvider>
              {children}
            </TrackingColumnsSortProvider>
          </TrackingColumnsFiltersProvider>
        </TrackingColumnsProvider>
      </ContactViewSearchProvider>
    </ContactViewProvider>
  );
};

const SplashPageWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <ClerkProviderWrapper pubKey={clerkPubKey}>
      <OrganizationUserProvider>
        <AlertProvider>
          <SubscriptionProvider>
            <GoogleOAuthProvider clientId={googleClientId}>
              <UserIntegrationsProvider>{children}</UserIntegrationsProvider>
            </GoogleOAuthProvider>
          </SubscriptionProvider>
        </AlertProvider>
      </OrganizationUserProvider>
    </ClerkProviderWrapper>
  );
};

const FairPayPageWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <ClerkProviderWrapper pubKey={clerkPubKey}>
      <AlertProvider>
        <GoogleOAuthProvider clientId={googleClientId}>
          {children}
        </GoogleOAuthProvider>
      </AlertProvider>
    </ClerkProviderWrapper>
  );
};

function AdminPage() {
  return (
    <AlertProvider>
      <SignedIn>
        <AdminUserProvider>
          <AdminHome />
        </AdminUserProvider>
      </SignedIn>
    </AlertProvider>
  );
}

function AppRoutes() {
  const router = createBrowserRouter([
    {
      path: "/",
      element: (
        <DashboardProviderWrapper>
          <SignedIn>
            <LandingPage loggedIn />
          </SignedIn>
          <SignedOut>
            <LandingPage loggedIn={false} />
          </SignedOut>
        </DashboardProviderWrapper>
      ),
      errorElement: <PageNotFound />,
    },
    {
      path: "/privacy-policy",
      element: <PrivacyPolicyPage />,
    },
    {
      path: "/webview-editor",
      element: <WebViewEditor />,
    },
    {
      path: "/annual/*",
      element: (
        <SplashPageWrapper>
          <AnnualSplashPage />
        </SplashPageWrapper>
      ),
    },
    {
      path: "/pitch-please/*",
      element: (
        <SplashPageWrapper>
          <PitchPlease />
        </SplashPageWrapper>
      ),
    },
    {
      path: "/fairpay/*",
      element: (
        <FairPayPageWrapper>
          <FairPaySurvey />
        </FairPayPageWrapper>
      ),
    },
    {
      path: "/pro-bundle/*",
      element: (
        <SplashPageWrapper>
          <ProPartnershipBundle />
        </SplashPageWrapper>
      ),
    },
    {
      path: "/va-money-back/*",
      element: (
        <SplashPageWrapper>
          <VaAnnualSplashPage hasBookCall={false} />
        </SplashPageWrapper>
      ),
    },
    {
      path: "/va-moneyback/*",
      element: (
        <SplashPageWrapper>
          <VaAnnualSplashPage hasBookCall={true} />
        </SplashPageWrapper>
      ),
    },
    {
      path: "/repitching/*",
      element: (
        <SplashPageWrapper>
          <Repitching />
        </SplashPageWrapper>
      ),
    },
    {
      path: "/pro/*",
      element: (
        <SplashPageWrapper>
          <ProPlanSplashPage />
        </SplashPageWrapper>
      ),
    },
    {
      path: "/va/*",
      element: (
        <SplashPageWrapper>
          <VaPlanSplashPage />
        </SplashPageWrapper>
      ),
    },
    {
      path: "/free-template/*",
      element: (
        <SplashPageWrapper>
          <AnnualTemplate />
        </SplashPageWrapper>
      ),
    },
    {
      path: "/strategy-package/*",
      element: (
        <SplashPageWrapper>
          <StrategyPackageSplashPage />
        </SplashPageWrapper>
      ),
    },
    {
      path: "accept-token",
      element: (
        <ClerkProviderWrapper pubKey={clerkPubKey}>
          <OrganizationUserProvider>
            <AlertProvider>
              <AcceptToken />
            </AlertProvider>
          </OrganizationUserProvider>
        </ClerkProviderWrapper>
      ),
    },
    {
      path: "sign-in/*",
      element: (
        <ClerkProviderWrapper pubKey={clerkPubKey}>
          <OrganizationUserProvider>
            <AlertProvider>
              <AuthPage
                action={AuthAction.SIGN_IN}
                prevLink={`${window.location.pathname}${window.location.search}${window.location.hash}`}
              />
            </AlertProvider>
          </OrganizationUserProvider>
        </ClerkProviderWrapper>
      ),
    },
    {
      path: "sign-up/*",
      element: (
        <ClerkProviderWrapper pubKey={clerkPubKey}>
          <OrganizationUserProvider>
            <AlertProvider>
              <AuthPage
                action={AuthAction.SIGN_UP}
                prevLink={`${window.location.pathname}${window.location.search}${window.location.hash}`}
              />
            </AlertProvider>
          </OrganizationUserProvider>
        </ClerkProviderWrapper>
      ),
    },
    {
      path: "/brands/*",
      element: (
        <SplashPageWrapper>
          <BrandOnboarding />
        </SplashPageWrapper>
      ),
    },
    {
      path: "/instagram-onboarding/*",
      element: (
        <SplashPageWrapper>
          <InstagramOnboarding />
        </SplashPageWrapper>
      ),
    },
    {
      path: "/start/sign-in/*",
      element: <Navigate to="/sign-in" replace />,
    },
    {
      path: "/start/sign-up/*",
      element: <Navigate to="/sign-up" replace />,
    },
    {
      path: "/",
      element: (
        <DashboardProviderWrapper>
          <SignedIn>
            <Outlet />
          </SignedIn>
          <SignedOut>
            <Navigate to="/sign-in" replace />
          </SignedOut>
        </DashboardProviderWrapper>
      ),
      children: [
        {
          path: "/start-onboarding/*",
          element: <OnboardingInfluencer />,
        },
        {
          path: "/onboard/*",
          element: <Onboarding />,
        },
        {
          path: "/va-onboarding-self-serve/*",
          element: <SelfServeVaOnboarding />,
        },
        {
          path: "/subscriptions/*",
          element: (
            <CheckoutProvider>
              <StripeCheckout />
            </CheckoutProvider>
          ),
        },
        {
          path: "checkout-success/*",
          element: <SuccessPage />,
        },
        {
          path: "/intake-forms/*",
          element: <AddOnTemplatesIntakeForm />,
          children: [
            {
              path: ":addOnItemId",
              element: <AddOnTemplatesIntakeForm />,
            },
          ],
        },
        {
          path: "/dashboard",
          element: <Home />,
          children: [
            {
              index: true,
              element: <Navigate to="/dashboard/home" replace />,
            },
            {
              path: "subscribe",
              element: <SubscribePage />,
            },
            {
              path: "inbox/*",
              element: <InfluencerContacts />,
            },
            {
              path: "recommendations/*",
              element: <RecommendationsPage />,
            },
            {
              path: "home/*",
              element: <HomePage />,
            },
            {
              path: "brands",
              element: <Discover />,
              children: [
                {
                  index: true,
                  element: <Navigate to="/dashboard/brands/all" replace />,
                },
                {
                  path: "all/*",
                  element: <DiscoverResults />,
                },
              ],
            },
            {
              path: "profile",
              element: <Profile />,
            },
            {
              path: "instagram/*",
              element: <InstagramDashboardPage />,
            },
            {
              path: "templates/*",
              element: <TemplatesPage />,
            },
            {
              path: "campaigns/*",
              element: (
                <CampaignsProvider>
                  <CampaignsPage />
                </CampaignsProvider>
              ),
            },
            {
              path: "local/*",
              element: (
                <CampaignsProvider>
                  <LocalPitching />
                </CampaignsProvider>
              ),
            },
            {
              path: "virtual-assistant/*",
              element: <VA />,
            },
            {
              path: "portfolio-sites/*",
              element: <PortfolioSitesPage />,
            },
            {
              path: "tracking/*",
              element: <DeprecatedPage />,
            },
            {
              path: "settings/*",
              element: <Settings />,
            },
          ],
        },
      ],
    },
    {
      path: "/admin",
      element: (
        <ClerkProviderWrapper pubKey={adminClerkPubKey}>
          <OrganizationUserProvider>
            <AdminPage />
          </OrganizationUserProvider>
          <SignedOut>
            <Layout body={<SignIn redirectUrl={`/admin`} />} />
          </SignedOut>
        </ClerkProviderWrapper>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="/admin/organizations" replace />,
        },
        {
          path: "csv",
          element: <AdminCsvUpload />,
        },
        { path: "contacts/*", element: <AdminContacts /> },
        { path: "brands/*", element: <AdminBrands /> },
        {
          path: "organizations/*",
          element: <AdminOrganization />,
        },
        {
          path: "elasticsearch",
          element: <AdminElasticsearch />,
        },
        {
          path: "migrations",
          element: <AdminMigrations />,
        },
        {
          path: "emails",
          element: <AdminReadEmail />,
        },
        {
          path: "scraping",
          element: <AdminScraping />,
        },
        {
          path: "user-requests",
          element: <UserRequests />,
        },
        {
          path: "brand-verifications/*",
          element: <AdminBrandVerifications />,
        },
        {
          path: "instagram",
          element: <InstagramPage />,
        },
        {
          path: "va-management/*",
          element: <VAManagement />,
        },
        {
          path: "search-brands/*",
          element: <SearchBrands />,
        },
        { path: "scraping-preview", element: <ScrapingPreview /> },
        { path: "clay", element: <AdminClay /> },
        { path: "upload-brands", element: <UploadBrands /> },
        { path: "merge-brands/*", element: <MergeBrands /> },
        { path: "upload-local", element: <UploadLocal /> },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
}

export default App;
